import React, { useState, useEffect, useRef } from "react";
import {
  HiBell,
  HiBellSlash,
  HiClock,
  HiExclamationTriangle,
} from "react-icons/hi2";
import { useReminder, useFCMTokenManagement } from "../hooks/useReminder";
import { notificationService } from "../services/notificationService";
import SearchableSelect from "./common/SearchableSelect";

interface PushNotificationSetupProps {
  className?: string;
  showUI?: boolean;
  onSkip?: () => void; // Callback for when user chooses to skip
  onContinue?: () => void; // Callback for when user continues to device setup
  showContinueButton?: boolean; // Whether to show the continue button section
  refreshDevice?: boolean; // When true, force refresh/re-register the device token even if already registered
}

const PushNotificationSetup: React.FC<PushNotificationSetupProps> = ({
  className = "",
  showUI = true,
  onSkip,
  onContinue,
  showContinueButton = false,
  refreshDevice = false,
}) => {
  const {
    reminder,
    permissionState,
    loading: reminderLoading,
    error: reminderError,
    requestPermission,
    setReminder,
    deleteReminder,
  } = useReminder();

  const {
    tokens,
    loading: tokensLoading,
    addCurrentDevice,
  } = useFCMTokenManagement();

  const [selectedHour, setSelectedHour] = useState<number>(
    reminder?.localHour || 8
  ); // Default to current reminder or 8 AM
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [setupComplete, setSetupComplete] = useState(false);
  const [registrationFailed, setRegistrationFailed] = useState(false); // Track if registration failed for retry UI

  // Ref to track if auto-registration has been attempted (prevents infinite retry loops)
  const hasAttemptedAutoRegistration = useRef(false);

  // Get current device ID to check if this specific device is registered
  const currentDeviceId = notificationService.getCurrentDeviceId();

  // Check if current device is already registered and enabled
  const currentDeviceToken = tokens.find(
    (token) => token.deviceId === currentDeviceId
  );
  const isCurrentDeviceRegistered = !!currentDeviceToken;
  const isCurrentDeviceEnabled = currentDeviceToken?.enabled || false;

  // Overall device counts (for display purposes)
  const enabledDevicesCount = tokens.filter((token) => token.enabled).length;

  // Check if setup is already complete (user has existing reminder and permission)
  // During onboarding (showContinueButton=true), we should allow users to set their reminder time
  // even if they already have permissions granted
  const isAlreadySetup =
    permissionState.granted &&
    isCurrentDeviceRegistered &&
    !!reminder &&
    !showContinueButton;

  // Update selected hour when reminder changes
  useEffect(() => {
    if (reminder?.localHour !== undefined) {
      setSelectedHour(reminder.localHour);
    }
  }, [reminder]);

  // Reset auto-registration attempt flag when device becomes registered
  // This allows for retries if the registration state changes
  useEffect(() => {
    if (isCurrentDeviceRegistered) {
      hasAttemptedAutoRegistration.current = false;
    }
  }, [isCurrentDeviceRegistered]);

  // Auto-register current device if permission is granted but current device is not registered
  useEffect(() => {
    const autoRegisterDevice = async () => {
  if (
    permissionState.granted &&
    !isCurrentDeviceRegistered &&
    !tokensLoading &&
    !isSettingUp &&
    !hasAttemptedAutoRegistration.current
  ) {
    hasAttemptedAutoRegistration.current = true;
    setIsSettingUp(true);
    setStatusMessage("Registering this device...");
    setRegistrationFailed(false);

    try {
      // ── ADD THIS: wait for messaging before registering ──
      const messagingReady = await notificationService.waitForMessagingReady();
      if (!messagingReady) {
        console.warn('[PushNotificationSetup] Messaging not ready, skipping auto-registration');
        hasAttemptedAutoRegistration.current = false; // allow retry
        setIsSettingUp(false);
        setStatusMessage('');
        return;
      }
      // ────────────────────────────────────────────────────

      await addCurrentDevice();
      setStatusMessage("Device registered successfully!");
      setRegistrationFailed(false);
      setTimeout(() => setStatusMessage(""), 4000);
    } catch (error) {
       console.error("Auto-registration failed:", error);
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          // Provide user-friendly error messages
          if (errorMessage.includes("timed out")) {
            setStatusMessage("Registration timed out. This can happen on slow connections or certain browsers.");
          } else {
            setStatusMessage("Failed to register device. Please try again.");
          }
          setRegistrationFailed(true);
    } finally {
      setIsSettingUp(false);
    }
  }
};

    autoRegisterDevice();
  }, [
    permissionState.granted,
    isCurrentDeviceRegistered,
    tokensLoading,
    isSettingUp,
    addCurrentDevice,
  ]);

  // Manual retry handler for when auto-registration fails
  const handleRetryRegistration = async () => {
    hasAttemptedAutoRegistration.current = false; // Reset to allow retry
    setRegistrationFailed(false);
    setStatusMessage("");
    setIsSettingUp(true);
    setStatusMessage("Retrying device registration...");

    try {
      await addCurrentDevice();
      setStatusMessage("Device registered successfully!");
      setRegistrationFailed(false);
      setTimeout(() => setStatusMessage(""), 4000);
    } catch (error) {
      console.error("Manual registration retry failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      if (errorMessage.includes("timed out")) {
        setStatusMessage("Registration timed out. Please check your connection and try again.");
      } else {
        setStatusMessage("Failed to register device. Please try again.");
      }
      setRegistrationFailed(true);
    } finally {
      setIsSettingUp(false);
    }
  };

  // Ref to track if refresh has been attempted for the current mount (prevents duplicate refreshes)
  const hasAttemptedRefresh = useRef(false);

  // Force refresh device token and reminder when refreshDevice prop is true
  // This bypasses normal registration checks to ensure the token is fresh
 useEffect(() => {
  const forceRefreshDevice = async () => {
    if (
      refreshDevice &&
      permissionState.granted &&
      !tokensLoading &&
      !isSettingUp &&
      !reminderLoading &&
      !hasAttemptedRefresh.current
    ) {
      console.log("[PushNotificationSetup] Force refreshing device token and reminder (refreshDevice=true)");
      
      // NEW: Wait for messaging to be ready with retries
      const messagingReady = await notificationService.waitForMessagingReady();
      
      if (!messagingReady) {
        console.warn("[PushNotificationSetup] Messaging not ready after retries, skipping refresh");
        return;
      }

      hasAttemptedRefresh.current = true;

      try {
        await addCurrentDevice();
        console.log("[PushNotificationSetup] Device token refreshed successfully");

        if (reminder?.localHour !== undefined) {
          await setReminder(reminder.localHour);
          console.log("[PushNotificationSetup] Reminder re-saved with existing time:", reminder.localHour);
        }
      } catch (error) {
        console.error("[PushNotificationSetup] Failed to refresh device token or reminder:", error);
      }
    }
  };

  forceRefreshDevice();
}, [
  refreshDevice,
  permissionState.granted,
  tokensLoading,
  isSettingUp,
  reminderLoading,
  reminder,
  addCurrentDevice,
  setReminder,
]);


  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label:
      i === 0
        ? "12:00 AM"
        : i < 12
        ? `${i}:00 AM`
        : i === 12
        ? "12:00 PM"
        : `${i - 12}:00 PM`,
  }));

  // Handle requesting permission, auto-registering device, and setting up reminder
  const handleAllowNotifications = async () => {
    setIsSettingUp(true);
    setStatusMessage("");

    try {
      // If permission is already granted, skip to setting reminder
      if (permissionState.granted) {
        // Make sure device is registered
        if (!isCurrentDeviceRegistered) {
          setStatusMessage("Setting up your device...");
          await addCurrentDevice();
        }

        // Set up reminder
        setStatusMessage("Setting up daily reminder...");
        await setReminder(selectedHour);
      } else {
        // Step 1: Request permission
        setStatusMessage("Requesting notification permission...");
        const granted = await requestPermission();

        if (!granted) {
          throw new Error("Notification permission is required for reminders");
        }

        // Step 2: Automatically register device
        setStatusMessage("Setting up your device...");
        await addCurrentDevice();

        // Step 3: Set up default reminder
        setStatusMessage("Setting up daily reminder...");
        await setReminder(selectedHour);
      }

      setStatusMessage("Push notifications are now active!");
      setSetupComplete(true);

      // Reset auto-registration flag on successful manual setup
      hasAttemptedAutoRegistration.current = false;

      // Clear success message after a delay
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Setup failed:", error);
      setStatusMessage(
        error instanceof Error
          ? error.message
          : "Setup failed. Please try again."
      );
    } finally {
      setIsSettingUp(false);
    }
  };

  // Handle updating reminder time
  const handleUpdateReminder = async () => {
    setIsSettingUp(true);
    try {
      await setReminder(selectedHour);
      setStatusMessage("Reminder time updated successfully!");
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Failed to update reminder time:", error);
      setStatusMessage("Failed to update reminder time. Please try again.");
    } finally {
      setIsSettingUp(false);
    }
  };

  // Handle removing reminder
  const handleRemoveReminder = async () => {
    try {
      await deleteReminder();
      setStatusMessage("Daily reminders have been disabled.");
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Failed to remove reminder:", error);
      setStatusMessage("Failed to remove reminder. Please try again.");
    }
  };

  const formatLocalTime = (hour: number) => {
    return hourOptions.find((opt) => opt.value === hour)?.label || `${hour}:00`;
  };

  return (
    <>
      {showUI && (
        <div className={`space-y-6 b ${className} `}>
          {/* Main Setup Flow */}
          {!isAlreadySetup && !setupComplete && (
            <div className="text-center space-y-6">
              <div></div>

              {/* Time selection */}
              <div className="max-w-sm mx-auto mb-6">
                <SearchableSelect
                  options={hourOptions.map((option) => ({
                    value: String(option.value),
                    label: option.label,
                  }))}
                  value={String(selectedHour)}
                  onChange={(value) => setSelectedHour(Number(value))}
                  placeholder="Search times..."
                  allOptionLabel="Select Time"
                  emptyValue=""
                  disabled={isSettingUp || setupComplete}
                  label="What time do you want daily reminders?"
                  inputClassName="bg-base-200"
                />
              </div>

              {/* Enable button when NOT in onboarding flow (no showContinueButton prop) */}
              {!showContinueButton && (!permissionState.granted || !reminder) && (
                <button
                  onClick={handleAllowNotifications}
                  disabled={isSettingUp || permissionState.denied}
                  className="bg-neutral text-neutral-content rounded-full font-grotesk py-3 px-6 flex items-center justify-center font-bold hover:bg-neutral/80 max-w-xs mx-auto w-full"
                >
                  {isSettingUp ? (
                    <>
                      <span className="loading loading-spinner loading-sm mr-2"></span>
                      Setting up...
                    </>
                  ) : (
                    <>Enable Daily Reminders</>
                  )}
                </button>
              )}

              {/* Status Message with Loading Spinner */}
              {statusMessage && (
                <div
                  className={`alert rounded-md max-w-md mx-auto flex items-center justify-center gap-2 ${
                    statusMessage.includes("failed") ||
                    statusMessage.includes("Failed") ||
                    statusMessage.includes("timed out") ||
                    statusMessage.includes("error")
                      ? "bg-red-500/15 text-red-700 dark:text-red-400"
                      : statusMessage.includes("successfully") ||
                        statusMessage.includes("active") ||
                        statusMessage.includes("registered")
                      ? "bg-green-500/15 text-green-700 dark:text-green-400"
                      : "bg-blue-500/15 text-blue-700 dark:text-blue-400"
                  }`}
                >
                  {isSettingUp && (
                    <span className="loading loading-spinner loading-sm"></span>
                  )}
                  <span className="text-sm">{statusMessage}</span>
                </div>
              )}

              {/* Retry button when registration fails */}
              {registrationFailed && !isSettingUp && (
                <button
                  onClick={handleRetryRegistration}
                  className="bg-neutral text-neutral-content rounded-full font-grotesk py-3 px-6 flex items-center justify-center font-bold hover:bg-neutral/80 max-w-xs mx-auto w-full"
                >
                  Retry Registration
                </button>
              )}

              {/* Permission blocked help */}
              {permissionState.denied && (
                <div className="bg-yellow-500/10 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
                  <div className="flex">
                    <HiExclamationTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                    <div className="text-sm text-left">
                      <p className="font-medium text-yellow-700 dark:text-yellow-400 mb-1">
                        Notifications Blocked
                      </p>
                      <p className="text-yellow-700 mb-2">
                        To enable notifications, please:
                      </p>
                      <ul className="list-disc list-inside text-yellow-700 space-y-1">
                        <li>
                          <strong>Chrome:</strong> Click the lock icon next to
                          the URL and allow notifications
                        </li>
                        <li>
                          <strong>Safari:</strong> Go to Settings → Websites →
                          Notifications
                        </li>
                        <li>
                          <strong>Firefox:</strong> Click the shield icon and
                          update notification settings
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STAGE 2: Everything Complete - Show management UI */}
          {(isAlreadySetup || setupComplete) && !isSettingUp && (
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-base-content mb-3">
                  Daily Reminders Active!
                </h2>
                <p className="text-base-content/80 font-grotesk">
                  You currently receive daily reminders at{" "}
                  <strong>{formatLocalTime(reminder!.localHour)}</strong>
                </p>
              </div>

              <div className="">
                <div className="space-y-4">
                  <div>
                    <SearchableSelect
                      options={hourOptions.map((option) => ({
                        value: String(option.value),
                        label: option.label,
                      }))}
                      value={String(selectedHour)}
                      onChange={(value) => setSelectedHour(Number(value))}
                      placeholder="Search times..."
                      allOptionLabel="Select Time"
                      emptyValue=""
                      disabled={isSettingUp}
                      label="Reminder Time"
                      inputClassName="bg-base-200"
                    />
                  </div>

                  <div className="flex justify-between font-heading">
                    <button
                      onClick={handleUpdateReminder}
                      disabled={
                        isSettingUp || selectedHour === reminder?.localHour
                      }
                      className="bg-neutral text-neutral-content rounded-md flex items-center px-4 py-2"
                    >
                      {isSettingUp ? (
                        <span className="loading loading-spinner loading-sm mr-2"></span>
                      ) : (
                        <HiClock className="w-4 h-4 mr-2" />
                      )}
                      Update Time
                    </button>
                    <button
                      onClick={handleRemoveReminder}
                      disabled={isSettingUp}
                      className="bg-red-500/10 px-4 text-red-600 hover:bg-red-500/10 flex items-center rounded-md"
                    >
                      <HiBellSlash className="w-4 h-4 mr-2" />
                      Disable
                    </button>
                  </div>
                </div>
              </div>
              {/* Status Message with Loading Spinner */}
              {statusMessage && (
                <div
                  className={`alert rounded-md flex items-center justify-center gap-2 ${
                    statusMessage.includes("failed") ||
                    statusMessage.includes("Failed") ||
                    statusMessage.includes("timed out") ||
                    statusMessage.includes("error")
                      ? "bg-red-500/15"
                      : statusMessage.includes("successfully") ||
                        statusMessage.includes("active") ||
                        statusMessage.includes("registered")
                      ? "bg-green-500/15"
                      : "bg-blue-500/15"
                  }`}
                >
                  {isSettingUp && (
                    <span className="loading loading-spinner loading-sm"></span>
                  )}
                  <span className="font-grotesk">{statusMessage}</span>
                </div>
              )}

              {/* Error Display */}
              {reminderError && (
                <div className="alert alert-error">
                  <span>{reminderError}</span>
                </div>
              )}

              {/* Device info */}
              {enabledDevicesCount > 0 && (
                <div className="text-left text-sm text-base-content/60">
                  Notifications active on <strong>{enabledDevicesCount}</strong>{" "}
                  device
                  {enabledDevicesCount !== 1 ? "s" : ""}
                  {!isCurrentDeviceEnabled && (
                    <div className="text-orange-600 mt-1">
                      Note: Reminders are disabled on this device
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Continue Button Section - positioned outside the white box */}
      {showContinueButton && (
        <>
          <div className="text-center mt-12">
            {!permissionState.granted || !reminder ? (
              // Show Enable Daily Reminders button when permission not granted OR reminder not set
              <button
                onClick={handleAllowNotifications}
                disabled={isSettingUp || permissionState.denied}
                className="bg-neutral text-neutral-content backdrop-blur-sm py-5 font-heading flex items-center justify-center font-bold btn-lg mx-auto rounded-full hover:bg-neutral/60"
              >
                {isSettingUp ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Setting up...
                  </>
                ) : (
                  <>
                    {permissionState.granted
                      ? "Set Daily Reminder"
                      : "Enable Daily Reminders"}
                  </>
                )}
              </button>
            ) : (
              // Show Continue to Device Setup only after both notifications are enabled AND reminder is set
              <button
                onClick={onContinue}
                disabled={isSettingUp}
                className="bg-neutral text-neutral-content rounded-md backdrop-blur-sm py-5 font-heading font-bold btn-lg w-full hover:bg-neutral/60 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSettingUp ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Setting up...
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            )}

            {/* Skip for now - only show when not setting up and permission not granted */}
            {onSkip && !isSettingUp && !permissionState.granted && (
              <div className="mt-10 mb-10">
                <button onClick={onSkip} className="font-grotesk">
                  Skip for now
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default PushNotificationSetup;
